/**
 * SKI AXIS · Site-wide urgency ticker.
 *
 * Scrolling strip showing all currently OPEN positions in the Governance
 * Collective + Chapter Leads + Cohort 1 + Forum CTAs. Appears above the
 * main navigation on every page.
 *
 * - CSS-only infinite horizontal marquee (track duplicated 2× for seamless loop)
 * - Hover pauses the animation
 * - Mobile adapts speed + sizing
 * - Cream/navy/gold palette matching the editorial system
 */
import React from 'react';

const STYLES = `
.skia-ticker{background:linear-gradient(90deg,#0D1B2A 0%,#1A2F4A 50%,#0D1B2A 100%);color:#FAF7F0;border-bottom:1px solid #C8A84B;overflow:hidden;position:relative;height:38px;display:flex;align-items:center;}
.skia-ticker::before,.skia-ticker::after{content:'';position:absolute;top:0;bottom:0;width:64px;z-index:2;pointer-events:none;}
.skia-ticker::before{left:0;background:linear-gradient(90deg,#0D1B2A 0%,transparent 100%);}
.skia-ticker::after{right:0;background:linear-gradient(270deg,#0D1B2A 0%,transparent 100%);}
.skia-ticker-eb{flex-shrink:0;padding:0 22px;font-size:10px;font-weight:700;letter-spacing:.26em;color:#0D1B2A;background:#C8A84B;height:100%;display:flex;align-items:center;text-transform:uppercase;z-index:3;}
.skia-ticker-eb .pulse{display:inline-block;width:6px;height:6px;background:#0D1B2A;border-radius:50%;margin-right:8px;animation:skia-tick-pulse 1.2s ease-in-out infinite;}
@keyframes skia-tick-pulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.3;transform:scale(1.4);}}
.skia-ticker-track{display:flex;white-space:nowrap;animation:skia-tick-scroll 68s linear infinite;will-change:transform;}
.skia-ticker:hover .skia-ticker-track{animation-play-state:paused;}
@keyframes skia-tick-scroll{0%{transform:translateX(0);}100%{transform:translateX(-50%);}}
.skia-ticker-item{display:inline-flex;align-items:center;gap:9px;padding:0 28px;font-size:12px;color:rgba(250,247,240,.88);letter-spacing:.04em;height:38px;}
.skia-ticker-item strong{color:#FAF7F0;font-weight:700;}
.skia-ticker-item .open{color:#C8A84B;font-weight:700;letter-spacing:.18em;text-transform:uppercase;font-size:10.5px;border:1px solid rgba(200,168,75,.45);padding:3px 9px;}
.skia-ticker-item .dot{color:#C8A84B;font-size:8px;opacity:.55;}
.skia-ticker-item .urgent{color:#C8A84B;font-weight:600;font-family:'Cormorant Display',Georgia,serif;font-style:italic;font-size:14px;}
.skia-ticker-item a{color:inherit;text-decoration:none;border-bottom:1px solid rgba(200,168,75,.3);padding-bottom:1px;transition:border-color .15s;}
.skia-ticker-item a:hover{border-color:#C8A84B;color:#C8A84B;}
@media(max-width:680px){.skia-ticker-eb{padding:0 14px;font-size:9px;letter-spacing:.2em;} .skia-ticker-track{animation-duration:48s;} .skia-ticker-item{padding:0 18px;font-size:11px;}}
`;

// Governance Collective + Chapter Leads — ALL positions vacant (Feb 2026)
const TICKER_ITEMS = [
  { label: 'Chapter Lead · Dubai', status: 'OPEN' },
  { label: 'Chapter Lead · London', status: 'OPEN', hot: true },
  { label: 'Chapter Lead · Amsterdam', status: 'OPEN', hot: true },
  { label: 'Chapter Lead · Mumbai', status: 'OPEN' },
  { label: 'Chapter Lead · Bangalore', status: 'OPEN' },
  { label: 'Chapter Lead · Riyadh', status: 'OPEN' },
  { label: 'Chapter Lead · Doha', status: 'OPEN' },
  { label: 'Chapter Lead · Singapore', status: 'OPEN' },
  { label: 'Chapter Lead · Kuala Lumpur', status: 'OPEN' },
  { label: 'Chapter Lead · Nairobi', status: 'OPEN' },
  { label: 'Chapter Lead · Lagos', status: 'OPEN' },
  { label: 'Mentors & Domain Experts', status: 'OPEN' },
  { label: 'Strategic Advisors', status: 'OPEN' },
  { label: 'Programme Heads', status: 'OPEN' },
  { cta: 'Apply to the Governance Collective →', path: '/chapters/apply' },
  { note: 'Cohort 1 · 20 Founder Seats · Applications close 30 June 2026' },
  { cta: 'Apply for Cohort 1 →', path: '/apply' },
  { note: 'Invest UAE Forum · 31 May 2026 · AED 149' },
];

const renderItem = (item, idx) => {
  if (item.cta) {
    return (
      <span key={idx} className="skia-ticker-item">
        <a href={item.path} data-testid={`ticker-cta-${item.path.replace(/[/]/g, '-')}-${idx}`}>
          <strong>{item.cta}</strong>
        </a>
        <span className="dot">◆</span>
      </span>
    );
  }
  if (item.note) {
    return (
      <span key={idx} className="skia-ticker-item">
        <span className="urgent">{item.note}</span>
        <span className="dot">◆</span>
      </span>
    );
  }
  return (
    <span key={idx} className="skia-ticker-item">
      <strong>{item.label}</strong>
      <span className="open">{item.status}</span>
      {item.hot && <span className="urgent">filling fast</span>}
      <span className="dot">◆</span>
    </span>
  );
};

export default function UrgencyTicker() {
  return (
    <>
      <style>{STYLES}</style>
      <div
        className="skia-ticker"
        role="marquee"
        aria-label="Currently open Chapter Lead and Governance Collective positions"
        data-testid="site-urgency-ticker"
      >
        <div className="skia-ticker-eb"><span className="pulse" />Now Appointing</div>
        <div className="skia-ticker-track">
          {TICKER_ITEMS.map((item, i) => renderItem(item, `a-${i}`))}
          {TICKER_ITEMS.map((item, i) => renderItem(item, `b-${i}`))}
        </div>
      </div>
    </>
  );
}
